import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Clock, CheckCircle2, Loader2, Mail, Building2, User, Phone, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API } from "@/utils/constants";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-amber-500/10 text-amber-700 border-amber-200" },
  contacted: { label: "Contacted", color: "bg-blue-500/10 text-blue-700 border-blue-200" },
  demo_scheduled: { label: "Demo Scheduled", color: "bg-purple-500/10 text-purple-700 border-purple-200" },
  converted: { label: "Converted", color: "bg-emerald-500/10 text-emerald-700 border-emerald-200" },
  closed: { label: "Closed", color: "bg-gray-500/10 text-gray-600 border-gray-200" },
};

export const DemoRequestsView = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchRequests = async () => {
    try {
      const r = await axios.get(`${API}/demo-requests`);
      setRequests(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchRequests(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/demo-requests/${id}/status`, { status });
      toast.success(`Marked as ${STATUS_CONFIG[status]?.label || status}`);
      fetchRequests();
    } catch { toast.error("Failed to update"); }
  };

  const filtered = filter === "all" ? requests : requests.filter(r => r.status === filter);
  const counts = { all: requests.length, pending: requests.filter(r => r.status === "pending").length, contacted: requests.filter(r => r.status === "contacted").length };

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2" data-testid="demo-requests-title">
          <Send className="h-5 w-5 text-primary" />Demo Requests
        </h2>
        <p className="text-sm text-muted-foreground">{requests.length} total, {counts.pending} pending</p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { key: "all", label: `All (${counts.all})` },
          { key: "pending", label: `Pending (${counts.pending})` },
          { key: "contacted", label: `Contacted (${counts.contacted})` },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === f.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Send className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{filter === "all" ? "No demo requests yet" : `No ${filter} requests`}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => {
            const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
            return (
              <div key={req.id} className="bg-card border rounded-xl p-4 hover:shadow-sm transition-shadow" data-testid={`demo-request-${req.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{req.name}</span>
                      <Badge variant="outline" className={`text-xs ${statusCfg.color}`}>{statusCfg.label}</Badge>
                      {req.followup_sent && <Badge variant="outline" className="text-xs bg-indigo-500/10 text-indigo-600 border-indigo-200">Follow-up sent</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <a href={`mailto:${req.email}`} className="flex items-center gap-1 hover:text-primary transition-colors"><Mail className="h-3 w-3" />{req.email}</a>
                      {req.company && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{req.company}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                    <Clock className="h-3 w-3" />
                    {new Date(req.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {req.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(req.id, "contacted")} className="text-xs h-7" data-testid={`mark-contacted-${req.id}`}>
                        <Phone className="h-3 w-3 mr-1" />Mark Contacted
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(req.id, "demo_scheduled")} className="text-xs h-7">
                        <Clock className="h-3 w-3 mr-1" />Demo Scheduled
                      </Button>
                    </>
                  )}
                  {req.status === "contacted" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(req.id, "demo_scheduled")} className="text-xs h-7">
                        <Clock className="h-3 w-3 mr-1" />Demo Scheduled
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(req.id, "converted")} className="text-xs h-7 text-emerald-600">
                        <CheckCircle2 className="h-3 w-3 mr-1" />Converted
                      </Button>
                    </>
                  )}
                  {req.status === "demo_scheduled" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(req.id, "converted")} className="text-xs h-7 text-emerald-600">
                      <CheckCircle2 className="h-3 w-3 mr-1" />Mark Converted
                    </Button>
                  )}
                  {req.status !== "closed" && req.status !== "converted" && (
                    <Button size="sm" variant="ghost" onClick={() => updateStatus(req.id, "closed")} className="text-xs h-7 text-muted-foreground">
                      <XCircle className="h-3 w-3 mr-1" />Close
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
